import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Paperclip } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import { supabase } from "@/integrations/supabase/client";
import { useAccountingMutations, useSuppliers } from "@/hooks/useAccounting";
import type {
  Expense, ExpenseCategory, ExpenseKind, ExpenseMethod,
} from "@/services/edgeFunctionWrappers/accountingManagementWrapper";

const CATEGORIES: ExpenseCategory[] = [
  "rent", "salaries", "suppliers", "equipment", "marketing", "maintenance", "utilities", "insurance", "other",
];
const METHODS: ExpenseMethod[] = ["cash", "card", "bank_transfer", "check"];

// Contrôlés AVANT soumission : au-delà, l'upload Storage échouerait après
// que l'écriture est créée.
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ACCEPTED_ATTACHMENT_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ligne à éditer — null pour une création. */
  expense?: Expense | null;
  /** Type par défaut à la création (onglet appelant). */
  defaultKind?: ExpenseKind;
  /** Coche « Récurrent chaque mois » à la création (onglet Dépenses récurrentes). */
  defaultRecurring?: boolean;
  /** Coachs proposés pour un salaire (id + nom), fournis par l'appelant. */
  coaches?: { id: string; name: string; hourlyRate?: number | null }[];
  /** Mois affiché (yyyy-MM) : la création d'un modèle récurrent génère aussitôt son occurrence pour ce mois. */
  periodMonth?: string;
}

interface FormState {
  kind: ExpenseKind;
  category: ExpenseCategory;
  label: string;
  amount: string;
  expense_date: string;
  status: "due" | "paid";
  payment_method: ExpenseMethod;
  due_date: string;
  supplier_id: string;
  invoice_number: string;
  employee_name: string;
  coach_id: string;
  recurring: boolean;
  notes: string;
}

const emptyForm = (kind: ExpenseKind, recurring = false): FormState => ({
  kind,
  category: kind === "salary" ? "salaries" : kind === "supplier_invoice" ? "suppliers" : "other",
  label: "",
  amount: "",
  expense_date: new Date().toISOString().split("T")[0],
  status: kind === "supplier_invoice" ? "due" : "paid",
  payment_method: "cash",
  due_date: "",
  supplier_id: "",
  invoice_number: "",
  employee_name: "",
  coach_id: "",
  recurring: recurring || kind === "salary",
  notes: "",
});

const fromExpense = (e: Expense): FormState => ({
  kind: e.kind,
  category: e.category,
  label: e.label,
  amount: String(e.amount),
  expense_date: e.expense_date,
  status: e.status === "paid" ? "paid" : "due",
  payment_method: e.payment_method ?? "cash",
  due_date: e.due_date ?? "",
  supplier_id: e.supplier_id ?? "",
  invoice_number: e.invoice_number ?? "",
  employee_name: e.employee_name ?? "",
  coach_id: e.coach_id ?? "",
  recurring: e.is_template,
  notes: e.notes ?? "",
});

/**
 * Saisie/édition d'une écriture (frais, facture fournisseur, salaire).
 * Dialog sur desktop, bottom sheet sur mobile (pattern PaymentDialog),
 * champs conditionnels selon le type, pièce jointe vers le bucket
 * supplier-invoices (gym_id/expense_id/fichier).
 */
export function ExpenseFormDialog({ open, onOpenChange, expense, defaultKind = "expense", defaultRecurring = false, coaches = [], periodMonth }: Props) {
  const { t } = useTranslation(["accounting", "common"]);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const gymId = useUserStore((s) => s.gymId);
  const { data: suppliers = [] } = useSuppliers();
  const { createExpense, updateExpense, generateRecurring } = useAccountingMutations();

  const [form, setForm] = useState<FormState>(() => emptyForm(defaultKind, defaultRecurring));
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const isEdit = !!expense;
  // Un modèle récurrent s'édite (montant, jour, libellé) mais son statut ne se
  // gère pas ici : due=actif / cancelled=désactivé, via l'onglet Dépenses.
  const isTemplateEdit = isEdit && !!expense?.is_template;

  useEffect(() => {
    if (open) {
      setForm(expense ? fromExpense(expense) : emptyForm(defaultKind, defaultRecurring));
      setFile(null);
    }
  }, [open, expense, defaultKind, defaultRecurring]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const uploadAttachment = async (expenseId: string): Promise<string | null> => {
    if (!file || !gymId) return null;
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `${gymId}/${expenseId}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from("supplier-invoices").upload(path, file);
    if (error) throw error;
    return path;
  };

  const onSubmit = async () => {
    const amountNum = Number(String(form.amount).replace(",", "."));
    if (!form.label.trim() || !Number.isFinite(amountNum) || amountNum < 0) {
      toast({ title: t("common:error"), description: t("form.invalid"), variant: "destructive" });
      return;
    }
    if (file && file.size > MAX_ATTACHMENT_BYTES) {
      toast({ title: t("common:error"), description: t("form.attachmentTooLarge"), variant: "destructive" });
      return;
    }
    if (file && !ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) {
      toast({ title: t("common:error"), description: t("form.attachmentBadType"), variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      let saved: Expense | null;
      if (isEdit && expense) {
        // Édition : les champs vidés sont envoyés à null (pas undefined,
        // que l'edge ignore) pour réellement les effacer en base.
        const patch: Partial<Expense> = {
          category: form.category,
          label: form.label.trim(),
          amount: amountNum,
          expense_date: form.expense_date,
          payment_method: form.payment_method,
          due_date: form.due_date || null,
          supplier_id: form.kind === "supplier_invoice" && form.supplier_id ? form.supplier_id : null,
          invoice_number: form.invoice_number || null,
          employee_name: form.kind === "salary" ? (form.employee_name || null) : null,
          coach_id: form.kind === "salary" && form.coach_id ? form.coach_id : null,
          notes: form.notes || null,
          ...(isTemplateEdit ? {} : { status: form.status }),
        };
        saved = await updateExpense.mutateAsync({ id: expense.id, patch });
      } else {
        saved = await createExpense.mutateAsync({
          kind: form.kind,
          category: form.category,
          label: form.label.trim(),
          amount: amountNum,
          expense_date: form.expense_date,
          status: form.status,
          payment_method: form.payment_method,
          due_date: form.due_date || undefined,
          supplier_id: form.kind === "supplier_invoice" && form.supplier_id ? form.supplier_id : undefined,
          invoice_number: form.invoice_number || undefined,
          employee_name: form.kind === "salary" ? (form.employee_name || undefined) : undefined,
          coach_id: form.kind === "salary" && form.coach_id ? form.coach_id : undefined,
          notes: form.notes || undefined,
          recurrence: form.recurring ? "monthly" : undefined,
        });
        // Un modèle fraîchement créé doit apparaître tout de suite dans le
        // mois affiché : génération immédiate (idempotente, fire-and-forget).
        if (form.recurring && saved) {
          generateRecurring.mutate(periodMonth, { onError: () => {} });
        }
      }

      if (saved && file) {
        try {
          const path = await uploadAttachment(saved.id);
          if (path) await updateExpense.mutateAsync({ id: saved.id, patch: { attachment_path: path } });
        } catch {
          // L'écriture est déjà enregistrée : re-soumettre créerait un
          // doublon. On ferme et on signale l'échec de la pièce jointe seule.
          toast({ title: t("form.attachmentFailed"), variant: "destructive" });
          onOpenChange(false);
          return;
        }
      }

      toast({ title: isEdit ? t("form.updated") : t("form.created") });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: t("common:error"), description: e?.message ?? "", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <div className="space-y-3">
      {/* Type d'écriture (création seulement) */}
      {!isEdit && (
        <div>
          <label className="text-sm font-medium">{t("form.kind")}</label>
          <Select value={form.kind} onValueChange={(v) => setForm(emptyForm(v as ExpenseKind))}>
            <SelectTrigger className="text-base md:text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="expense">{t("kinds.expense")}</SelectItem>
              <SelectItem value="supplier_invoice">{t("kinds.supplier_invoice")}</SelectItem>
              <SelectItem value="salary">{t("kinds.salary")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <label className="text-sm font-medium">{t("form.label")}</label>
        <Input value={form.label} onChange={(e) => set("label", e.target.value)} className="text-base md:text-sm" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-sm font-medium">{t("form.amount")}</label>
          <Input
            inputMode="decimal"
            placeholder="0.000"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            className="text-base md:text-sm"
          />
        </div>
        <div>
          <label className="text-sm font-medium">{t("form.date")}</label>
          <Input type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)} className="text-base md:text-sm" />
        </div>
      </div>

      {form.kind !== "salary" && (
        <div>
          <label className="text-sm font-medium">{t("form.category")}</label>
          <Select value={form.category} onValueChange={(v) => set("category", v as ExpenseCategory)}>
            <SelectTrigger className="text-base md:text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {form.kind === "supplier_invoice" && (
        <>
          <div>
            <label className="text-sm font-medium">{t("form.supplier")}</label>
            <Select value={form.supplier_id || "__none__"} onValueChange={(v) => set("supplier_id", v === "__none__" ? "" : v)}>
              <SelectTrigger className="text-base md:text-sm"><SelectValue placeholder={t("form.noSupplier")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("form.noSupplier")}</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm font-medium">{t("form.invoiceNumber")}</label>
              <Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="text-base md:text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("form.dueDate")}</label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="text-base md:text-sm" />
            </div>
          </div>
        </>
      )}

      {form.kind === "salary" && (
        <>
          <div>
            <label className="text-sm font-medium">{t("form.coach")}</label>
            <Select
              value={form.coach_id || "__free__"}
              onValueChange={(v) => {
                if (v === "__free__") { set("coach_id", ""); return; }
                const coach = coaches.find((c) => c.id === v);
                setForm((f) => ({
                  ...f,
                  coach_id: v,
                  employee_name: coach?.name ?? f.employee_name,
                  label: f.label || t("form.salaryLabel", { name: coach?.name ?? "" }),
                }));
              }}
            >
              <SelectTrigger className="text-base md:text-sm"><SelectValue placeholder={t("form.freeEmployee")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__free__">{t("form.freeEmployee")}</SelectItem>
                {coaches.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.hourlyRate ? ` (${c.hourlyRate}/h)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">{t("form.employeeName")}</label>
            <Input value={form.employee_name} onChange={(e) => set("employee_name", e.target.value)} className="text-base md:text-sm" />
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2">
        {/* Un modèle ne se paie pas : son statut (actif/désactivé) se gère
            depuis l'onglet Salaires, pas ici. */}
        {!isTemplateEdit && (
          <div>
            <label className="text-sm font-medium">{t("form.status")}</label>
            <Select value={form.status} onValueChange={(v) => set("status", v as "due" | "paid")}>
              <SelectTrigger className="text-base md:text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">{t("statuses.paid")}</SelectItem>
                <SelectItem value="due">{t("statuses.due")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className={isTemplateEdit ? "col-span-2" : undefined}>
          <label className="text-sm font-medium">{t("form.method")}</label>
          <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v as ExpenseMethod)}>
            <SelectTrigger className="text-base md:text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METHODS.map((m) => (
                <SelectItem key={m} value={m}>{t(`methods.${m}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Récurrence mensuelle : réservée aux salaires (création seulement —
          un template se gère ensuite depuis l'onglet Salaires). */}
      {!isEdit && form.kind === "salary" && (
        <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
          <div>
            <div className="text-sm font-medium">{t("form.recurring")}</div>
            <div className="text-xs text-muted-foreground">{t("form.recurringHint")}</div>
          </div>
          <Switch checked={form.recurring} onCheckedChange={(v) => set("recurring", v)} />
        </div>
      )}

      {/* Pièce jointe */}
      <div>
        <label className="text-sm font-medium flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5" /> {t("form.attachment")}
        </label>
        <Input
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-sm"
        />
        {expense?.attachment_path && !file && (
          <p className="mt-1 text-xs text-muted-foreground truncate">{t("form.attachmentExisting")}</p>
        )}
      </div>

      <div>
        <label className="text-sm font-medium">{t("form.notes")}</label>
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="min-h-[60px] text-base md:text-sm" />
      </div>
    </div>
  );

  const footer = (
    <div className="shrink-0 border-t bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-row gap-2">
      <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => onOpenChange(false)} disabled={saving}>
        {t("common:cancel")}
      </Button>
      <Button type="button" className="flex-1 h-10" onClick={onSubmit} disabled={saving}>
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {isEdit ? t("form.save") : t("form.add")}
      </Button>
    </div>
  );

  const title = isEdit ? t("form.editTitle") : t("form.addTitle");
  const description = t("form.description");

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-3xl p-0 flex flex-col max-h-[92dvh]">
          <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-muted" aria-hidden />
          <SheetHeader className="shrink-0 px-4 pt-2 pb-2 text-left">
            <SheetTitle className="text-xl">{title}</SheetTitle>
            <SheetDescription className="text-xs">{description}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 py-2">{body}</div>
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 flex flex-col max-h-[90dvh] overflow-hidden">
        <DialogHeader className="shrink-0 px-6 pt-4 pb-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-2">{body}</div>
        {footer}
      </DialogContent>
    </Dialog>
  );
}
